/**
 * Glossary UI (W2-22, 2026-06-11) — the app-wide plain-English tooltip system.
 *
 * Three exports, one shared popover:
 * - <GlossaryProvider>   per-scope first-use tracking (mount once per page
 *   scope — the orchestrator mounts it in AppShell keyed by pathname; chat
 *   wraps each assistant message in its own so a long conversation still
 *   expands first uses per message). GRACEFULLY OPTIONAL: with no provider,
 *   GlossaryText tracks per-instance and a bare GlossaryTerm expands itself.
 * - <GlossaryTerm>       one known term — dotted-teal trigger (or the legacy
 *   ⓘ icon via variant="icon") + the popover / mobile bottom sheet.
 * - <GlossaryText>       auto-wraps every known term in a plain string
 *   (PROSE default: first use per scope also renders a grey inline
 *   " (Expansion)"; pass inlineFirstUse={false} for DENSE contexts).
 *
 * Popover anatomy per the approved mockup
 * (docs/mockups/2026-06-11-w2-22-glossary-tooltips.html): bold
 * "ABBR — Expansion" title with the abbr in teal, plain-English line,
 * grey technical line behind a top border, mono-teal citation links.
 * Surface is slate-900 in light mode, slate-800 + slate-600 border in dark.
 * Mobile (no hover OR <640px): tap opens a fixed bottom sheet with a handle
 * bar (≥44px close affordances), overlay/Esc closes, body scroll locked.
 *
 * Positioning uses position:fixed + a portal to document.body so the popover
 * is never clipped by overflow-hidden/truncate ancestors and never overflows
 * a 375px viewport (left is clamped, width caps at calc(100vw - 24px)).
 *
 * Rules-of-hooks note (Landmine B in the audit): the old InfoTooltip called
 * useEffect after an early return. Here every component that calls hooks
 * calls them unconditionally — unknown keys are handled by a hook-free
 * dispatch wrapper (GlossaryTerm) before the stateful inner mounts.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { getEntry, matchGlossaryTerms, type GlossaryEntry } from '@/lib/glossary';

// ---------------------------------------------------------------------------
// first-use scope
// ---------------------------------------------------------------------------

interface GlossaryScope {
  /** Returns true exactly once per key per scope (mutates a ref Set — never re-renders). */
  markSeen: (key: string) => boolean;
}

const GlossaryScopeContext = createContext<GlossaryScope | null>(null);

/**
 * Mount once per first-use scope. Re-keys (or pass a changing `scopeKey`) to
 * reset — e.g. the orchestrator mounts <GlossaryProvider scopeKey={pathname}>
 * in AppShell so each route gets fresh inline expansions.
 */
export function GlossaryProvider({ scopeKey, children }: { scopeKey?: string; children: ReactNode }) {
  const seenRef = useRef<Set<string>>(new Set());
  const lastKeyRef = useRef(scopeKey);
  if (lastKeyRef.current !== scopeKey) {
    lastKeyRef.current = scopeKey;
    seenRef.current = new Set();
  }
  const value = useMemo<GlossaryScope>(
    () => ({
      markSeen: (key: string) => {
        if (seenRef.current.has(key)) return false;
        seenRef.current.add(key);
        return true;
      },
    }),
    [],
  );
  return <GlossaryScopeContext.Provider value={value}>{children}</GlossaryScopeContext.Provider>;
}

// ---------------------------------------------------------------------------
// popover vs bottom sheet
// ---------------------------------------------------------------------------

const SHEET_QUERY = '(hover: none), (max-width: 639px)';

function computeMode(): 'popover' | 'sheet' {
  // jsdom has no matchMedia — default to popover so tests exercise the desktop path.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'popover';
  return window.matchMedia(SHEET_QUERY).matches ? 'sheet' : 'popover';
}

function useGlossaryMode(): 'popover' | 'sheet' {
  const [mode, setMode] = useState<'popover' | 'sheet'>(computeMode);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(SHEET_QUERY);
    const onChange = () => setMode(mq.matches ? 'sheet' : 'popover');
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mode;
}

// ---------------------------------------------------------------------------
// shared card content (popover + sheet render the same anatomy)
// ---------------------------------------------------------------------------

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function citationLinkLabel(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    if (host === 'law.cornell.edu') return 'Cornell LII';
    if (host === 'irs.gov') return 'IRS.gov';
    if (host === 'uscis.gov') return 'USCIS.gov';
    return host;
  } catch {
    return 'Source';
  }
}

function GlossaryCard({ entry }: { entry: GlossaryEntry }) {
  return (
    <>
      <p className="font-body text-[13.5px] font-bold text-white mb-1.5">
        {entry.expansion ? (
          <>
            <span className="text-primary-300">{entry.term}</span>
            {' — '}
            {entry.expansion}
          </>
        ) : (
          capitalize(entry.term)
        )}
      </p>
      <p className="text-xs leading-relaxed text-slate-100">{entry.plainEnglish}</p>
      <p className="mt-2 pt-2 border-t border-slate-700 text-[11px] leading-relaxed text-slate-400">
        {entry.technical}
      </p>
      {(entry.ircRef || entry.url) && (
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          {entry.ircRef && (
            <span className="font-mono text-[11px] text-primary-300">{entry.ircRef}</span>
          )}
          {entry.url && (
            <a
              href={entry.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[11px] text-primary-300 hover:underline"
            >
              {citationLinkLabel(entry.url)} ↗
            </a>
          )}
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// GlossaryTerm — trigger + popover/sheet
// ---------------------------------------------------------------------------

export interface GlossaryTermProps {
  /** canonical glossary key (e.g. 'qbi') */
  entryKey: string;
  /** trigger label — defaults to the entry's canonical term */
  children?: ReactNode;
  /**
   * PROSE contexts (default true): the first occurrence per scope also renders
   * a grey inline " (Expansion)". DENSE contexts (tables/chips/labels) pass
   * false — hover-only, and the occurrence does NOT consume the scope's
   * first-use slot (a table shouldn't steal the expansion from later prose).
   */
  inlineFirstUse?: boolean;
  /** 'underline' = dotted-teal in-prose trigger; 'icon' = legacy ⓘ (InfoTooltip) */
  variant?: 'underline' | 'icon';
}

/**
 * Hook-free dispatch: unknown keys render bare children (the completeness
 * lint makes an unknown key a TEST failure, so this fallback is for runtime
 * resilience only — never a silent design state).
 */
export function GlossaryTerm({ entryKey, children, inlineFirstUse = true, variant = 'underline' }: GlossaryTermProps) {
  const entry = getEntry(entryKey);
  if (!entry) return <>{children}</>;
  return (
    <GlossaryTermInner entry={entry} inlineFirstUse={inlineFirstUse} variant={variant}>
      {children}
    </GlossaryTermInner>
  );
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

interface PopoverPos {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
}

function GlossaryTermInner({
  entry,
  children,
  inlineFirstUse,
  variant,
}: {
  entry: GlossaryEntry;
  children?: ReactNode;
  inlineFirstUse: boolean;
  variant: 'underline' | 'icon';
}) {
  const scope = useContext(GlossaryScopeContext);
  const mode = useGlossaryMode();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<PopoverPos | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tipId = useId();

  const labelText = typeof children === 'string' ? children : entry.term;

  // First-use is decided ONCE per mount (ref-guarded so re-renders and
  // StrictMode double-renders are idempotent, and an instance that won the
  // inline expansion keeps it for its lifetime).
  const firstUseRef = useRef<boolean | null>(null);
  if (firstUseRef.current === null) {
    if (!inlineFirstUse || !entry.expansion) {
      firstUseRef.current = false;
    } else {
      // If the matched text already IS the spelled-out form, appending
      // " (Expansion)" would be silly — but the reader has now seen it, so
      // still consume the scope slot.
      const alreadySpelled = labelText.trim().toLowerCase() === entry.expansion.trim().toLowerCase();
      const first = scope ? scope.markSeen(entry.key) : true;
      firstUseRef.current = first && !alreadySpelled;
    }
  }
  const showInline = firstUseRef.current === true;

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openNow = useCallback(() => {
    cancelClose();
    const el = triggerRef.current;
    if (el && typeof window !== 'undefined') {
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = Math.min(340, vw - 24);
      const left = clamp(rect.left, 12, Math.max(12, vw - width - 12));
      // Flip above when there's not enough room below (mirrors the old
      // InfoTooltip heuristic, but viewport-clamped on both axes).
      const placeAbove = rect.bottom > vh - 280 && rect.top > 280;
      setPos(
        placeAbove
          ? { left, width, bottom: vh - rect.top + 8 }
          : { left, width, top: rect.bottom + 8 },
      );
    }
    setOpen(true);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  }, [cancelClose]);

  // Esc closes popover + sheet.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // position:fixed goes stale on scroll — close instead of chasing.
  useEffect(() => {
    if (!open || mode !== 'popover') return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open, mode]);

  // Body scroll lock while the sheet is up.
  useEffect(() => {
    if (!open || mode !== 'sheet') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, mode]);

  // Clear any pending close timer on unmount.
  useEffect(() => cancelClose, [cancelClose]);

  const toggle = useCallback(() => {
    if (open) {
      cancelClose();
      setOpen(false);
    } else {
      openNow();
    }
  }, [open, cancelClose, openNow]);

  const hoverHandlers =
    mode === 'popover'
      ? {
          onMouseEnter: openNow,
          onMouseLeave: scheduleClose,
          onFocus: openNow,
          onBlur: scheduleClose,
        }
      : {};

  const sharedAria = {
    'aria-expanded': open,
    'aria-describedby': open ? tipId : undefined,
  };

  const titleText = entry.expansion ? `${entry.term} — ${entry.expansion}` : capitalize(entry.term);

  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          ref={(el) => {
            triggerRef.current = el;
          }}
          className="inline-flex items-center text-slate-400 hover:text-primary-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 rounded-sm"
          aria-label={`About ${titleText}`}
          onClick={toggle}
          {...sharedAria}
          {...hoverHandlers}
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      ) : (
        <span
          role="button"
          tabIndex={0}
          ref={(el) => {
            triggerRef.current = el;
          }}
          className="cursor-help font-semibold text-primary-700 dark:text-primary-400 border-b-[1.5px] border-dotted border-primary-500 rounded-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60"
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle();
            }
          }}
          {...sharedAria}
          {...hoverHandlers}
        >
          {children ?? entry.term}
        </span>
      )}

      {showInline && (
        <span className="text-slate-500 dark:text-slate-400 font-normal"> ({entry.expansion})</span>
      )}

      {open &&
        mode === 'popover' &&
        pos &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            role="tooltip"
            id={tipId}
            style={{
              position: 'fixed',
              left: pos.left,
              width: pos.width,
              ...(pos.top !== undefined ? { top: pos.top } : {}),
              ...(pos.bottom !== undefined ? { bottom: pos.bottom } : {}),
              zIndex: 60,
            }}
            className="rounded-xl bg-slate-900 dark:bg-slate-800 dark:border dark:border-slate-600 p-3.5 shadow-xl"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <GlossaryCard entry={entry} />
          </div>,
          document.body,
        )}

      {open &&
        mode === 'sheet' &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label={titleText}>
            <div
              className="absolute inset-0 bg-slate-900/60"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <div
              className="absolute bottom-0 left-0 right-0 rounded-t-2xl bg-slate-900 dark:bg-slate-800 dark:border-t dark:border-x dark:border-slate-600 px-4 shadow-2xl"
              style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}
            >
              {/* Handle bar doubles as a ≥44px close affordance (h-11). */}
              <button
                type="button"
                className="w-full h-11 flex items-center justify-center"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                <span className="w-10 h-1 rounded-full bg-slate-600" />
              </button>
              <div className="pb-2" id={tipId}>
                <GlossaryCard entry={entry} />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

// ---------------------------------------------------------------------------
// GlossaryText — auto-wrap a plain string
// ---------------------------------------------------------------------------

export interface GlossaryTextProps {
  /** the raw backend/prose string — NEVER pre-rendered nodes */
  text: string;
  /** PROSE default true; DENSE contexts (tables/chips/list rows) pass false */
  inlineFirstUse?: boolean;
}

/**
 * Runs the matcher over a plain string and splices <GlossaryTerm> spans in.
 * With no surrounding GlossaryProvider it creates a per-instance scope so a
 * term repeated within the SAME text still expands only once.
 */
export function GlossaryText({ text, inlineFirstUse = true }: GlossaryTextProps) {
  const scope = useContext(GlossaryScopeContext);
  const matches = useMemo(() => matchGlossaryTerms(text), [text]);

  // Per-instance fallback scope (created once; only used when no provider).
  const localScopeRef = useRef<GlossaryScope | null>(null);
  if (!scope && !localScopeRef.current) {
    const seen = new Set<string>();
    localScopeRef.current = {
      markSeen: (key: string) => {
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      },
    };
  }

  if (matches.length === 0) return <>{text}</>;

  const nodes: ReactNode[] = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (m.start > cursor) nodes.push(text.slice(cursor, m.start));
    nodes.push(
      <GlossaryTerm key={`${m.key}-${i}`} entryKey={m.key} inlineFirstUse={inlineFirstUse}>
        {m.matched}
      </GlossaryTerm>,
    );
    cursor = m.end;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));

  const content = <>{nodes}</>;
  if (scope) return content;
  return (
    <GlossaryScopeContext.Provider value={localScopeRef.current}>
      {content}
    </GlossaryScopeContext.Provider>
  );
}
